import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { useStore } from "../stores/store";
import { router } from "../router/Routes";
import i18n from "../i18n";
import { UserRegisterDto } from "../models/userRegisterDto";
import { User } from "../models/user";
import { Member, MemberFormValues } from "../models/member";
import { Payment, PaymentFormValues } from "../models/payment";
import { FamilyMember, FamilyMemberFormValues } from "../models/familyMember";
import { Activity, ActivityFormValues } from "../models/activity";
import { ActivityLog } from "../models/activityLog";
import { Tenant } from "../models/tenant";
import { DataTableFilterProps } from "../common/interfaces/TableData";
import { Participant, ParticipantFormValues } from "../models/participant";
import { ParticipantFilter } from "../models/participantFilter";
import { CreateParticipant } from "../models/createParticipant";
import { NewMember } from "../common/interfaces/NewMember";
import { ChangePassword } from "../common/interfaces/ChangePassword";
import { MemberDocument } from "../common/interfaces/MemberDocument";
import { NotificationEmail, NotificationEmailFormValues, PendingReminderSettings, UpdatePendingReminderSettings } from "../models/emailNotification";
import { MembershipPaymentByYearDto } from "../models/MembershipPaymentByYearDto";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const getRetryAfterMinutes = (headers: AxiosResponse["headers"]): number | null => {
    const retryAfter = headers?.["retry-after"];
    const retryAfterValue = Array.isArray(retryAfter) ? retryAfter[0] : retryAfter;
    const retryAfterSeconds = Number.parseInt(retryAfterValue ?? "", 10);

    if (Number.isNaN(retryAfterSeconds) || retryAfterSeconds <= 0) {
        return null;
    }

    return Math.max(1, Math.ceil(retryAfterSeconds / 60));
};

const extractErrorPayload = async (data: unknown): Promise<string> => {
    if (!data) return "";

    if (typeof data === "string") return data;

    if (data instanceof Blob) {
        const text = await data.text();
        if (!text) return "";

        try {
            const parsed = JSON.parse(text);
            return parsed?.message || parsed?.details || text;
        } catch {
            return text;
        }
    }

    try {
        const maybeObj = data as Record<string, unknown>;
        const message = maybeObj?.message as string | undefined;
        const details = maybeObj?.details as string | undefined;
        return message || details || JSON.stringify(data);
    } catch {
        return "";
    }
};

axios.interceptors.request.use((config) => {
    const token = useStore.getState().commonStore.token;
    if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

axios.interceptors.response.use(
    async (response) => response,
    async (error: AxiosError) => {
        try {
            const { data, status, config } = error?.response as AxiosResponse;
            let errorMessage = "";

            switch (status) {
                case 400: {
                    if (config.method === "get" && data.errors.hasOwnProperty("id")) {
                        errorMessage = i18n.t("errors.badRequest");
                    }
                    if (data.errors) {
                        const modalStateErrors = [];
                        for (const key in data.errors) {
                            if (data.errors[key]) {
                                modalStateErrors.push(data.errors[key]);
                            }
                        }
                        modalStateErrors.flat();
                        errorMessage = modalStateErrors.join('\n');
                    } else {
                        errorMessage = data.errors?.join('\n') || i18n.t("errors.badRequest");
                    }
                } break;
                case 401:
                    errorMessage = i18n.t("errors.unauthorized");
                    break;
                case 403: {
                    errorMessage = i18n.t("errors.forbidden");
                } break;
                case 404: {
                    errorMessage = i18n.t("errors.notFound");
                    router.navigate("/not-found");
                } break;
                case 429: {
                    const retryAfterMinutes = getRetryAfterMinutes((error.response as AxiosResponse | undefined)?.headers ?? {});
                    errorMessage = retryAfterMinutes
                        ? i18n.t("errors.tooManyRequestsWithWait", { minutes: retryAfterMinutes })
                        : i18n.t("errors.tooManyRequests");
                } break;
                case 500: {
                    const payloadText = await extractErrorPayload(error.response?.data);
                    errorMessage = payloadText || i18n.t("errors.serverError");
                    useStore.getState().commonStore.setServerError(data);
                } break;
            }
            console.log(errorMessage);
            toast.error(errorMessage);
            return Promise.reject(error);
        } catch (error) {
            console.error("Error in response interceptor:", error);
            return Promise.reject(error);
        }
    }
);

const requests = {
    get: <T>(url: string, config?: AxiosRequestConfig) => axios.get<T>(url, config).then(responseBody),
    post: <T>(url: string, body: {}, config?: AxiosRequestConfig) => axios.post<T>(url, body, config).then(responseBody),
    put: <T>(url: string, body: {}, config?: AxiosRequestConfig) => axios.put<T>(url, body, config).then(responseBody),
    patch: <T>(url: string, body: {}, config?: AxiosRequestConfig) => axios.patch<T>(url, body, config).then(responseBody),
    del: <T>(url: string, config?: AxiosRequestConfig) => axios.delete<T>(url, config).then(responseBody),
    postBlob: (url: string, body: {}, config?: AxiosRequestConfig) =>
        axios.post(url, body, { ...config, responseType: "blob" }).then((response) => response.data as Blob),
    getBlob: (url: string, config?: AxiosRequestConfig) =>
        axios.get(url, { ...config, responseType: "blob" }).then((response) => response.data as Blob),
};

export interface ListData<T> {
    data: T[];
    totalRecords: number;
    currentPage: number;
    pageSize: number;
    orderBy: string;
}

const Members = {
    list: (body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<Member>>("/members/list", body),
    details: (id: string) => requests.get<Member>(`/members/${id}`),
    exportXlsx: (body: DataTableFilterProps = { filters: {} }) => requests.post<void>(`/members/xlsx-export`, body),
    create: (member: MemberFormValues) => requests.post<void>(`/members`, member),
    update: (member: MemberFormValues) => requests.put<void>(`/members/${member.id}`, member),
    delete: (id: string) => requests.del<void>(`/members/${id}`),
    getPendingMembers: (body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<Member>>("/members/pending-members", body),
    getUnpaidMemberships: (body: DataTableFilterProps) => requests.post<ListData<Member>>("/members/unpaid-memberships", body),
    getPaidMemberships: (body: DataTableFilterProps) => requests.post<ListData<Member>>("/members/paid-memberships", body),
    uploadMembers: (data: FormData) => requests.post<any[]>(`/members/upload-xlsx-data`, data, { headers: { "Content-Type": "multipart/form-data" }, }),
    getXlsxHeaders: (data: FormData) => requests.post<string[]>(`/members/upload-xlsx-headers`, data, { headers: { "Content-Type": "multipart/form-data" }, }),
    register: (member: NewMember) => requests.post<void>(`/members/registration`, member),
    approveMember: (id: string) => requests.put<void>(`/members/approve-member/${id}`, {}),
    getUnpaidMembershipsXlsxExport: (body: DataTableFilterProps = { filters: {} }) => requests.post<void>("/members/unpaid-memberships/xlsx-export", { ...body, pageSize: 0 }),
    getPaidMembershipsXlsxExport: (body: DataTableFilterProps = { filters: {} }) => requests.post<void>("/members/paid-memberships/xlsx-export", { ...body, pageSize: 0 }),
    getMembershipPaymentsByYearAndMember: (memberId: string, body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) =>
        requests.post<ListData<MembershipPaymentByYearDto>>(
            `/members/membership-payments-by-year-and-member/${memberId}`, body
        ),
    downloadRegistrationFormPdf: async (member: MemberFormValues) => {
        const blob = await requests.getBlob(`/members/${member.id}/registration-form-pdf`);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Mitgliedsantrag-${(member as any)?.memberNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    },
    getDuplicates: () => axios.get("/members/duplicates").then(res => res.data),
    mergeMembers: (dto: { keepMemberId: string, mergeMemberId: string }) =>
        axios.post("/members/merge", dto),
    archiveDuplicates: (dto: { keepMemberId: string, archiveMemberIds: string[] }) =>
        axios.post("/members/archive-duplicates", dto),
};

const FamilyMembers = {
    list: (id: string, body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<FamilyMember>>(`/familyMembers/member/${id}`, body),
    details: (id: string) => requests.get<FamilyMember>(`/familyMembers/${id}`),
    create: (familyMember: FamilyMemberFormValues) => requests.post<void>(`/familyMembers`, familyMember),
    update: (familyMember: FamilyMemberFormValues) => requests.put<void>(`/familyMembers/${familyMember.id}`, familyMember),
    delete: (id: string) => requests.del<void>(`/familyMembers/${id}`),
};

const Payments = {
    list: (id: string, body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<Payment>>(`/payments/member/${id}`, body),
    exportXlsx: (body: DataTableFilterProps = { filters: {} }) => requests.post<void>(`/payments/unlinked/xlsx-export`, body),
    getUnlinkedMemberPayments: (body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<Payment>>(`/payments/unlinked-payments`, body),
    details: (id: string) => requests.get<Payment>(`/payments/${id}`),
    create: (payment: PaymentFormValues) => requests.post<void>(`/payments`, payment),
    update: (payment: PaymentFormValues) => requests.put<void>(`/payments/${payment.id}`, payment),
    delete: (id: string) => requests.del<void>(`/payments/${id}`),
    uploadPayments: (data: FormData) => requests.post<any[]>(`/payments/upload-data/`, data, { headers: { "Content-Type": "multipart/form-data" }, }),
    connectUnlinkedPayments: () => requests.put<void>(`/payments/connect-unlinked-payments`, {}),
    generateDonationProof: (id: string, filters: {}) => requests.post<any>(`/payments/member/${id}/donation-proof`, filters, { responseType: 'text' })
};

const Activities = {
    list: (body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<Activity>>(`/activities/list`, body),
    exportXlsx: (body: DataTableFilterProps = { filters: {} }) => requests.post<void>(`/activities/xlsx-export`, body),
    details: (id: string) => requests.get<Activity>(`/activities/${id}`),
    create: (activity: ActivityFormValues) => requests.post<void>(`/activities`, activity),
    update: (activity: ActivityFormValues) => requests.put<void>(`/activities/${activity.id}`, activity),
    delete: (id: string) => requests.del<void>(`/activities/${id}`),
};

const ActivityLogs = {
    getMemberActivityLogs: (memberId: string, body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<ActivityLog>>(`/activityLogs/member/${memberId}`, body),
    getEmailTemplateActivityLogs: (body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<ActivityLog>>(`/activityLogs/email-templates`, body),
};

const Users = {
    list: (body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<User>>(`/users`, body),
    current: () => requests.get<User>("users/current-user"),
    login: (user: UserRegisterDto) => requests.post<User>("/users/login", user),
    register: (user: UserRegisterDto) => requests.post<User>("/users/register", user),
    delete: (id: string) => requests.del<void>(`/users/${id}`),
    changePassword: (data: ChangePassword) => requests.patch<boolean>(`/users/change-password`, data),
};


const Tenants = {
    list: (params: URLSearchParams) => requests.get<Tenant[]>(`/tenants`, { params }),
    publicList: () => requests.get<{
        id: string;
        name: string;
        title: string;
        domain: string;
        logo: string;
        minimumMembershipFee: number;
        individualMembershipFee: number;
        familyMembershipFee: number;
        allowSepaPayments: boolean;
    }[]>(`/tenants/public`),
    getCurrent: () => requests.get<Tenant>(`/tenants/current`),
    updateCurrent: (tenant: Tenant) => requests.put<void>(`/tenants/current`, tenant),
    details: (id: string) => requests.get<Tenant>(`/tenants/${id}`),
    create: (tenant: Tenant) => requests.post<void>(`/tenants`, tenant),
    update: (tenant: Tenant) => requests.put<void>(`/tenants/${tenant.id}`, tenant),
    delete: (id: string) => requests.del<void>(`/tenants/${id}`),
};

const Participants = {
    list: (id: string, body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<Participant>>(`/participants/activity/${id}`, body),
    details: (id: string) => requests.get<Participant>(`/participants/${id}`),
    search: (name: string) => requests.get<ParticipantFilter[]>(`/participants/search/${name}`),
    create: (participant: CreateParticipant) => requests.post<void>(`/participants`, participant),
    update: (participant: ParticipantFormValues) => requests.put<void>(`/participants/${participant.id}`, participant),
    delete: (id: string) => requests.del<void>(`/participants/${id}`)
};

const Documents = {
    list: (id: string, body: DataTableFilterProps = { filters: {}, page: 1, pageSize: 10 }) => requests.post<ListData<MemberDocument>>(`/documents/member/${id}/list`, body),
    upload: (data: FormData) => requests.post<any[]>(`/documents`, data, { headers: { "Content-Type": "multipart/form-data" }, }),
    delete: (id: string) => requests.del<void>(`/documents/${id}`),
    downloadDocument: (id: string) => requests.get<Blob>(`/documents/${id}/download`, { responseType: "blob" }),
};

const Statistics = {
    getDashboardData: () => requests.get<any>("/statistics/dashboard"),
    getTotalMembers: () => requests.get<number>("/statistics/total-members"),
    getTotalActiveMembers: () => requests.get<number>("/statistics/total-active-members"),
    getTotalInactiveMembers: () => requests.get<number>("/statistics/total-inactive-members"),
    getTotalArchivedMembers: () => requests.get<number>("/statistics/total-archived-members"),
    getTotalPendingMembers: () => requests.get<number>("/statistics/total-pending-members"),
    getTotalMaleMembers: () => requests.get<number>("/statistics/total-male-members"),
    getTotalFemaleMembers: () => requests.get<number>("/statistics/total-female-members"),
    getTotalMembersIncludingFamily: () => requests.get<number>("/statistics/total-members-including-family"),
    getTotalMembershipPaymentsByYear: () => requests.get<{ year: number, totalPayments: number }[]>("/statistics/total-membership-payments-per-year"),
    getTotalZekatPaymentsPerYear: () => requests.get<{ year: number, totalPayments: number }[]>("/statistics/total-zekat-payments-per-year"),
    getTotalMembersByMembershipFee: () => requests.get<{ membershipGroupFee: number, totalMembers: number }[]>("/statistics/total-members-by-membershipfee"),
    getTotalMembersPerYear: () => requests.get<{ year: number, totalMembers: number }[]>("/statistics/total-members-per-year"),
    getNewMembersLast12Months: () => requests.get<number>("/statistics/new-members-last-12-months"),
    getNewMembersByMonth: () => requests.get<{ monthLabel: string, count: number }[]>("/statistics/new-members-by-month"),
    getTotalMembershipPaymentsPerYear: () => requests.get<{ year: number, totalPayments: number }[]>("/statistics/total-membership-payments-per-year"),
};

const NotificationEmails = {
    list: () => requests.get<NotificationEmail[]>("/notificationEmails"),
    add: (emailDto: NotificationEmailFormValues) => requests.post<void>("/notificationEmails", emailDto),
    remove: (id: string) => requests.del<void>(`/notificationEmails/${id}`),
    getReminderSettings: () => requests.get<PendingReminderSettings>("/notificationEmails/reminder-settings"),
    updateReminderSettings: (settings: UpdatePendingReminderSettings) => requests.put<PendingReminderSettings>("/notificationEmails/reminder-settings", settings),
    runReminderNow: () => requests.post<void>("/notificationEmails/reminder-settings/run-now", {}),
};

const Emails = {
    getTemplates: () => requests.get<any[]>("/emails/templates"),
    createTemplate: (template: any) => requests.post<void>("/emails/templates", template),
    updateTemplate: (template: any) => requests.put<void>(`/emails/templates/${template.id}`, template),
    deleteTemplate: (id: string) => requests.del<void>(`/emails/templates/${id}`),
    send: (payload: { memberIds: string[]; templateId?: string; body?: string; variables?: Record<string, string> }) =>
        requests.post<void>("/emails/send", payload),
    getLogs: () => requests.get<any[]>("/emails/logs"),
    downloadLogPdf: (id: string) =>
        requests.get<Blob>(`/emails/logs/${id}/pdf`, { responseType: "blob" }),
    getLayoutSettings: () => requests.get<any>("/emails/layout-settings"),
    saveLayoutSettings: (settings: { header: string; footer: string }) =>
        requests.post<void>("/emails/layout-settings", settings),
    generateLetterPdf: (data: { memberId: string, templateId?: string, body?: string, variables?: Record<string, string> }) =>
        requests.postBlob("/emails/generate-letter-pdf", data),
    generateBatchLettersPdf: (data: { memberIds: string[], templateId?: string, body?: string, variables?: Record<string, string> }) =>
        requests.postBlob("/emails/generate-batch-letters-pdf", data),
    getLettersForMember: (memberId: string) => requests.get<any[]>(`/emails/letters/member/${memberId}`),
    getBatchIds: () => requests.get<string[]>(`/emails/letters/batches`),
    downloadBatchPdf: (batchId: string) => requests.getBlob(`/emails/letters/batch/${batchId}/pdf`),
    downloadLetterPdf: (letterId: string) => requests.getBlob(`/emails/letters/${letterId}/pdf`),
    getBatchInfos: () => requests.get<any[]>(`/emails/letters/batches/infos`),
    deleteBatch: (batchId: string) => requests.del<void>(`/emails/letters/batch/${batchId}`),
};

const Backup = {
    downloadDatabase: () => requests.getBlob("/backup/database"),
};

const agent = {
    Members,
    Users,
    Payments,
    FamilyMembers,
    Activities,
    ActivityLogs,
    Tenants,
    Participants,
    Documents,
    Statistics,
    NotificationEmails,
    Emails,
    Backup,
};

export default agent;
